import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    CreateFixedAudience as CreateAudienceMutation,
    CreateFixedAudienceVariables as CreateAudienceVariables,
    AudienceCreateInput,
    GetAudiences as GetAudiencesQuery,
} from 'nutshell-graphql-types';

import {DEFAULT_GET_AUDIENCE_VARIABLES} from '../../entity-list-sidebar/consts';

import CREATE_AUDIENCE from '../mutations/create-audience.graphql';
import GET_AUDIENCES from '../queries/get-audiences.graphql';

export const useCreateAudience = (
    searchNameQuery?: string | null
): {
    createAudience: (arg1: AudienceCreateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [createAudience, {loading, error}] = useMutation<
        CreateAudienceMutation,
        CreateAudienceVariables
    >(CREATE_AUDIENCE);

    return {
        createAudience: (input: AudienceCreateInput) =>
            createAudience({
                variables: {audience: input},
                update: (proxy, mutationResults) => {
                    const cacheResult: GetAudiencesQuery | null | undefined = proxy.readQuery({
                        query: GET_AUDIENCES,
                        variables: {
                            ...DEFAULT_GET_AUDIENCE_VARIABLES,
                            search: searchNameQuery,
                        },
                    });

                    const newAudience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceCreate &&
                        mutationResults.data.audienceCreate.audience;

                    if (!newAudience) return;

                    if (cacheResult) {
                        const data = {
                            audiences: {
                                ...cacheResult.audiences,
                                edges: [
                                    {
                                        node: newAudience,
                                        __typename: 'AudiencesEdge',
                                    },
                                    // @ts-expect-error
                                ].concat(cacheResult.audiences.edges),
                            },
                        } as const;

                        proxy.writeQuery({
                            variables: {
                                ...DEFAULT_GET_AUDIENCE_VARIABLES,
                                search: searchNameQuery,
                            },
                            query: GET_AUDIENCES,
                            data: data,
                        });
                    }
                },
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
